import type { ChartResult, ChartSpec } from '../../../types/chart.js'
import { Sparkline } from '../Sparkline.js'
import { useNls } from '../../../nls/nlsProvider.js'
import './chart.css'

export interface NumberChartProps {
  spec: ChartSpec
  data: ChartResult
  className?: string
  testId?: string
}

export function NumberChart({
  spec,
  data,
  className = '',
  testId,
}: NumberChartProps) {
  const { t } = useNls()
  // Primary value: total, or the last point from the first series
  const points = data.series[0]?.points || []
  const lastPointValue = points.length > 0 ? points[points.length - 1].y : 0
  const mainValue = typeof data.total === 'number' ? data.total : lastPointValue

  // Delta calculation vs previous period
  const hasPrevious = typeof data.previousTotal === 'number'
  const delta = hasPrevious ? mainValue - (data.previousTotal as number) : null
  const deltaPct = hasPrevious && (data.previousTotal as number) !== 0
    ? Math.round((delta as number / (data.previousTotal as number)) * 100)
    : null

  // Sparkline values: up to 7 recent points
  const sparklineValues: Array<number | null> = points.map((p) => p.y)

  return (
    <div className={`chartNumberContainer ${className}`} data-testid={testId}>
      <div className="chartNumberValue" data-testid="chart-number-main-value">
        {mainValue.toLocaleString()}
      </div>

      {hasPrevious && delta !== null && (
        <div
          className={`chartNumberDelta ${delta >= 0 ? 'chartNumberDeltaUp' : 'chartNumberDeltaDown'}`}
          data-testid="chart-number-delta"
        >
          <span>{delta >= 0 ? `+${delta.toLocaleString()}` : delta.toLocaleString()}</span>
          {deltaPct !== null && <span>({deltaPct >= 0 ? `+${deltaPct}%` : `${deltaPct}%`})</span>}
          <span>{t('NLSChartPreviousPeriod')}</span>
        </div>
      )}

      {sparklineValues.length > 1 && (
        <div className="chartNumberSparkline" data-testid="chart-number-sparkline">
          <Sparkline
            values={sparklineValues}
            ariaLabel={`${spec.title || 'Metric'} trend`}
            height={28}
          />
        </div>
      )}
    </div>
  )
}
